import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Avatar } from '../ui/Avatar';
import { MaterialIcon } from '../ui/MaterialIcon';
import { Logo } from '../obros/Logo';

// Barra superior — solo desktop (lg+). En mobile, cada pantalla tiene su propio header
// (como en el diseño Claude) y la navegación vive en BottomNav, así que mostrar esto
// también en mobile duplicaba el header y quedaba raro.
export const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/worker/jobs?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  if (isAuthPage) return null;

  const clientLinks = [{ label: 'Mis trabajos', href: '/my-jobs' }];

  const workerLinks = [
    { label: 'Buscar trabajos', href: '/worker/jobs' },
    { label: 'Mis propuestas', href: '/my-jobs' },
  ];

  const adminLinks = [
    { label: 'Dashboard', href: '/admin/dashboard' },
    { label: 'Usuarios', href: '/admin/users' },
    { label: 'Trabajos', href: '/admin/jobs' },
  ];

  const getNavLinks = () => {
    if (!isAuthenticated) return [];
    if (user?.user_type === 'admin') return adminLinks;
    if (user?.user_type === 'worker') return workerLinks;
    return clientLinks;
  };

  const navLinks = getNavLinks();

  return (
    <nav className="hidden lg:block sticky top-0 z-50 bg-surface border-b border-outlineVarWarm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="shrink-0">
              <Logo className="h-7" />
            </Link>

            {navLinks.length > 0 && (
              <div className="flex items-center gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith(link.href)
                        ? 'text-primary-700 bg-cream'
                        : 'text-onSurface-var hover:text-onSurface hover:bg-surface-dim'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 max-w-md mx-6">
            <form onSubmit={handleSearch} className="relative w-full">
              <MaterialIcon name="search" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-onSurface-mute" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar trabajos, oficios..."
                className="w-full pl-10 pr-4 py-2 text-sm rounded-full border border-outlineVarWarm bg-surface-dim text-onSurface placeholder-onSurface-mute focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
              />
            </form>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {isAuthenticated ? (
              <>
                <Link to="/messages" className="p-2.5 rounded-full text-onSurface-var hover:bg-surface-dim transition-colors">
                  <MaterialIcon name="forum" size={20} />
                </Link>
                <button className="relative p-2.5 rounded-full text-onSurface-var hover:bg-surface-dim transition-colors">
                  <MaterialIcon name="notifications" size={20} />
                  <span className="absolute top-2 right-2 w-2 h-2 bg-primary-500 rounded-full border-2 border-surface" />
                </button>

                <div className="relative ml-1" ref={menuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-surface-dim transition-colors"
                  >
                    <Avatar src={user?.avatar} name={user?.full_name} size="sm" />
                    <MaterialIcon name="expand_more" size={18} className={`text-onSurface-mute transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-surface rounded-2xl shadow-lg border border-outlineVarWarm py-1.5 animate-fade-in">
                      <div className="px-4 py-2.5 border-b border-outlineVarWarm">
                        <p className="text-sm font-semibold text-onSurface">{user?.full_name}</p>
                        <p className="text-xs text-onSurface-mute">{user?.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-onSurface-var hover:bg-surface-dim transition-colors"
                      >
                        <MaterialIcon name="person" size={18} className="text-onSurface-mute" /> Mi perfil
                      </Link>
                      <Link
                        to="/"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-onSurface-var hover:bg-surface-dim transition-colors"
                      >
                        <MaterialIcon name="dashboard" size={18} className="text-onSurface-mute" /> Inicio
                      </Link>
                      <div className="border-t border-outlineVarWarm mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
                        >
                          <MaterialIcon name="logout" size={18} /> Cerrar sesión
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="px-4 py-2 text-sm font-medium text-onSurface-var hover:text-onSurface transition-colors">
                  Iniciar sesión
                </Link>
                <Link to="/register" className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-full hover:bg-primary-600 transition-colors">
                  Registrarse
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
